import {RiverBreakout, Text, Link, Stack, Statistic, Card, Box} from '@primer/react-brand'
import {renderWithNoMarks, renderPoints} from '../../utils'
import type {RiverBreakout as RiverBreakoutProps} from '../../types'
import {t} from '@github-ui/swp-core/lib/utils/localization'

export type FlexSuiteRiverBreakoutProps = RiverBreakoutProps['fields']

export const FlexSuiteRiverBreakout = ({
  heading,
  includeCta,
  label,
  href,
  alt,
  supportingContent,
  pointOne,
  pointTwo,
  pointThree,
  statistics,
  customerStories,
}: FlexSuiteRiverBreakoutProps) => {
  let trailingContent
  if (supportingContent === 'Points') {
    trailingContent = () => (
      <Stack>
        {pointOne && renderPoints(pointOne)}
        {pointTwo && renderPoints(pointTwo)}
        {pointThree && renderPoints(pointThree)}
      </Stack>
    )
  }

  if (supportingContent === 'Statistics') {
    trailingContent = () => {
      const stats = statistics?.map(stat => {
        const id = stat.sys.id
        const {heading: statHeading, description} = stat.fields
        return (
          <Statistic key={id}>
            <Statistic.Heading weight="normal" size="800" style={{color: 'var(--brand-color-text-emphasized)'}}>
              {renderWithNoMarks(statHeading)}
            </Statistic.Heading>
            <Statistic.Description>{renderWithNoMarks(description)}</Statistic.Description>
          </Statistic>
        )
      })

      return (
        <Stack gap="normal" padding="none">
          <Box padding="normal">{stats}</Box>
        </Stack>
      )
    }
  }
  if (supportingContent === 'Customer Stories' && customerStories?.length) {
    /*
     * This is a temporary implementation for customer stories.
     * It will be replaced when the proper component is ready in the Brand Refactor.
     */
    const stories = customerStories.map(story => {
      const id = story.sys.id
      const {logo, lead, url, title} = story.fields
      return (
        <Box key={id} padding="condensed">
          <Card href={url} variant="minimal" fullWidth disableAnimation ctaText={t('Learn more')}>
            <Card.Image src={logo.fields.file.url} alt={logo.fields.description || title} height={40} />
            <Card.Heading>{title}</Card.Heading>
            <Card.Description>{lead}</Card.Description>
          </Card>
        </Box>
      )
    })

    trailingContent = () => <Stack gap="normal">{stories}</Stack>
  }
  return (
    <RiverBreakout variant="gridline">
      {/* Will add visual content here after https://github.com/github/marketing-engineering/issues/5468 */}
      <RiverBreakout.Visual>
        <div
          style={{
            width: '100%',
            height: '100px',
            backgroundColor: 'var(--brand-color-canvas-inset)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          VISUAL COMING SOON
        </div>
        {/* Visual */}
      </RiverBreakout.Visual>
      <RiverBreakout.Content {...(trailingContent && {trailingComponent: trailingContent})}>
        {[
          <Text key={heading.nodeType}>{renderWithNoMarks(heading)}</Text>,
          ...(includeCta && href && label
            ? [
                <Link key={href} href={href} {...(alt ? {'aria-label': alt} : {})}>
                  {label}
                </Link>,
              ]
            : []),
        ]}
      </RiverBreakout.Content>
    </RiverBreakout>
  )
}
